<template>
  <v-main>
    <v-container fluid>
      <!-- Professional Page Header -->
      <v-row class="mb-1">
        <v-col cols="12">
          <v-card elevation="2" class="page-header-card">
            <v-card-title class="py-1 px-2">
              <v-row class="w-100 ma-0" align="center">
                <!-- Left Side: Title and Description -->
                <v-col cols="12" md="8" class="pa-0">
                  <div class="d-flex align-center">
                    <div>
                      <h1 class="page-title primary--text">Edit Appointment</h1>
                      <p class="page-subtitle grey--text text--darken-1 mb-0">
                        Modify appointment details, schedule, and status information
                      </p>
                    </div>
                  </div>
                </v-col>

                <!-- Right Side: Edit Toggle and Back Button -->
                <v-col cols="12" md="4" class="pa-0">
                  <div class="d-flex align-center justify-end">
                    <!-- Edit/Read-Only Toggle Button -->
                    <v-btn
                      :color="isEditMode ? 'warning' : 'primary'"
                      :outlined="!isEditMode"
                      @click="toggleEditMode"
                      class="mr-2"
                      small
                    >
                      <v-icon left>{{ isEditMode ? 'mdi-eye' : 'mdi-pencil' }}</v-icon>
                      {{ isEditMode ? 'Read Only' : 'Edit Mode' }}
                    </v-btn>
                    
                    <v-btn
                      color="grey darken-1"
                      outlined
                      @click="$router.back()"
                      class="back-button"
                      small
                    >
                      <v-icon left>mdi-arrow-left</v-icon>
                      Back to Appointments
                    </v-btn>
                  </div>
                </v-col>
              </v-row>
            </v-card-title>
          </v-card>
        </v-col>
      </v-row>

      <!-- Professional Form Card -->
      <v-row>
        <v-col cols="12">
          <v-card elevation="3" class="form-card">
            <!-- Form Header -->
            <v-card-title class="form-header py-1 px-2">
              <h2 class="form-title">Appointment Information</h2>
              <v-spacer></v-spacer>
              <v-chip 
                :color="isEditMode ? 'success' : 'warning'" 
                :outlined="!isEditMode" 
                x-small
              >
                <v-icon left x-small>{{ isEditMode ? 'mdi-pencil' : 'mdi-eye' }}</v-icon>
                {{ isEditMode ? 'Edit Mode' : 'Read Only Mode' }}
              </v-chip>
            </v-card-title>

            <v-divider></v-divider>
            <!-- Form Content -->
            <v-card-text class="form-content px-2 py-2" v-if="!isLoading">
          <v-row>
            <v-col sm="6">
              <v-autocomplete
                ref="doctor"
                outlined
                v-model="form.doctor_id"
                :items="doctors"
                label="Doctor *"
                prepend-inner-icon="mdi-doctor"
                :disabled="!isEditMode"
                :readonly="!isEditMode"
                :clearable="isEditMode"
                :error-messages="errors.doctor_id"
              ></v-autocomplete>
            </v-col>
            <v-col sm="6">
              <v-autocomplete
                ref="patient"
                outlined
                v-model="form.patient_id"
                :items="patients"
                label="Patient *"
                prepend-inner-icon="mdi-account-heart"
                :disabled="!isEditMode"
                :readonly="!isEditMode"
                :clearable="isEditMode"
                :error-messages="errors.patient_id"
              ></v-autocomplete>
            </v-col>
            <v-col sm="6">
              <v-text-field
                v-model="form.order_number"
                outlined
                label="Order Number"
                prepend-inner-icon="mdi-identifier"
                :disabled="!isEditMode"
                :readonly="!isEditMode"
                :error-messages="errors.order_number"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-menu
                ref="dateTimeMenu"
                v-model="dateTimeMenu"
                :close-on-content-click="false"
                transition="scale-transition"
                offset-y
                min-width="auto"
                :disabled="!isEditMode"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field
                    outlined
                    v-model="appointmentTimeFormatted"
                    label="Appointment Time"
                    prepend-inner-icon="mdi-calendar-clock"
                    :disabled="!isEditMode"
                    readonly
                    v-bind="attrs"
                    v-on="isEditMode ? on : {}"
                    :error-messages="errors.appointment_time"
                    :clearable="isEditMode"
                    @click:clear="clearAppointmentTime"
                  ></v-text-field>
                </template>
                <v-row class="ma-0">
                  <v-col>
                    <v-date-picker
                      v-model="appointmentDate"
                      @change="updateDateTime"
                    ></v-date-picker>
                  </v-col>
                  <v-col>
                    <v-time-picker
                      v-model="appointmentTime"
                      format="24hr"
                      @change="updateDateTime"
                    ></v-time-picker>
                  </v-col>
                </v-row>
              </v-menu>
            </v-col>
            <v-col sm="6">
              <v-autocomplete
                ref="status"
                outlined
                v-model="form.status"
                :items="statuses"
                label="Appointment Status"
                prepend-inner-icon="mdi-calendar-check"
                :disabled="!isEditMode"
                :readonly="!isEditMode"
                :error-messages="errors.status"
              ></v-autocomplete>
            </v-col>
            <v-col sm="12">
              <v-textarea
                v-model="form.notes"
                outlined
                label="Notes"
                prepend-inner-icon="mdi-note-text"
                :disabled="!isEditMode"
                :readonly="!isEditMode"
                :error-messages="errors.notes"
                rows="3"
                auto-grow
                :hint="isEditMode ? 'Add any additional notes or instructions for the appointment' : ''"
              ></v-textarea>
            </v-col>
          </v-row>
              
              <!-- Form Actions Footer -->
              <v-divider></v-divider>
              <v-card-actions class="form-actions pa-2" v-if="isEditMode">
                <v-row class="w-100 ma-0" align="center">
                  <v-col cols="12" md="6" class="pa-0">
                    <div class="d-flex align-center">
                      <span class="text-caption grey--text">
                        All required fields must be completed
                      </span>
                    </div>
                  </v-col>
                  
                  <v-col cols="12" md="6" class="pa-0">
                    <div class="d-flex justify-end">
                      <v-btn
                        color="error"
                        outlined
                        class="mr-3 action-button"
                        @click="confirmDelete"
                        :disabled="isSaving"
                      >
                        <v-icon left>mdi-delete</v-icon>
                        Delete
                      </v-btn>
                      <v-btn
                        color="grey"
                        outlined
                        class="mr-3 action-button"
                        @click="$router.back()"
                        :disabled="isSaving"
                      >
                        Cancel
                      </v-btn>
                      <v-btn
                        color="primary"
                        class="action-button"
                        @click="save"
                        :loading="isSaving"
                        :disabled="isSaving"
                      >
                        <v-icon left>mdi-content-save</v-icon>
                        Update Appointment
                      </v-btn>
                    </div>
                  </v-col>
                </v-row>
              </v-card-actions>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Booking Lifecycle Card -->
      <v-row v-if="!isLoading">
        <v-col cols="12">
          <v-card elevation="3">
            <v-card-title class="py-2 px-4">
              <v-icon color="primary" class="mr-2" size="20">mdi-timeline-clock</v-icon>
              <span class="text-body-1 font-weight-medium">Booking Lifecycle</span>
              <v-spacer></v-spacer>
              <status-chip x-small :color="appointmentLifecycle.find(s => s.active)?.color || 'grey'" :label="String(appointmentLifecycle.find(s => s.active)?.label || '-')" />
            </v-card-title>
            <v-divider></v-divider>
            <v-card-text class="px-6 py-5">
              <div class="d-flex align-center" style="overflow-x: auto; min-height: 80px;">
                <template v-for="(step, i) in appointmentLifecycle">
                  <div :key="`step-${i}`" class="d-flex flex-column align-center" style="min-width: 90px;">
                    <v-avatar
                      :color="step.active ? step.color : step.done ? step.color : 'grey lighten-3'"
                      size="38"
                    >
                      <v-icon :color="step.done || step.active ? 'white' : 'grey lighten-1'" small>
                        {{ step.icon }}
                      </v-icon>
                    </v-avatar>
                    <span
                      class="text-caption text-center mt-1 font-weight-medium"
                      :class="step.active ? `${step.color}--text` : step.done ? 'primary--text' : 'grey--text'"
                      style="max-width: 85px; line-height: 1.2;"
                    >{{ step.label }}</span>
                  </div>
                  <div
                    v-if="i < appointmentLifecycle.length - 1"
                    :key="`line-${i}`"
                    style="flex: 1; height: 2px; min-width: 24px; margin-bottom: 20px; border-radius: 1px;"
                    :style="{ background: appointmentLifecycle[i + 1].done || appointmentLifecycle[i + 1].active ? '#1976D2' : '#E0E0E0' }"
                  ></div>
                </template>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

        <!-- Loading State -->
        <v-row v-if="isLoading">
          <v-col cols="12">
            <v-card elevation="3" class="form-card">
              <v-card-text class="py-12 text-center">
                <health-loader message="Loading appointment details..." />
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      
      <!-- Delete Confirmation Dialog -->
      <v-dialog v-model="deleteDialog" max-width="500">
        <v-card rounded="xxl">
          <v-card-title class="headline">Confirm Delete</v-card-title>
          <v-card-text>
            Are you sure you want to delete this appointment? This action cannot be undone.
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey" text @click="deleteDialog = false">
              Cancel
            </v-btn>
            <v-btn color="error" @click="deleteAppointment">
              Delete
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-container>
  </v-main>
</template>

<script>
import axios from "@/axios";
import { splitIstDateTime, combineIstDateTime, APP_TIMEZONE } from "@/utils/dateTime";
export default {
  name: "AppointmentEditPage",
  data() {
    return {
      form: {
        doctor_id: "",
        patient_id: "",
        order_number: "",
        appointment_time: "",
        status: false,
        notes: "",
        is_active: true,
      },
      errors: {},
      doctors: [],
      patients: [],
      statuses: [
        { id: "PENDING", text: "Pending", value: false },
        { id: "CONFIRMED", text: "Confirmed", value: true },
      ],
      isLoading: false,
      isSaving: false,
      dateTimeMenu: false,
      appointmentDate: "",
      appointmentTime: "",
      deleteDialog: false,
      isEditMode: false,
    };
  },
  computed: {
    appointmentTimeFormatted() {
      if (this.form.appointment_time) {
        return new Date(this.form.appointment_time).toLocaleString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
          hour: "2-digit",
          minute: "2-digit",
          timeZone: APP_TIMEZONE,
        });
      }
      return "";
    },
    appointmentLifecycle() {
      const s = parseInt(this.form.status ?? -1);
      const terminalMap = {
        3: { label: 'Cancelled by User',   icon: 'mdi-account-cancel', color: 'error' },
        4: { label: 'Cancelled by Doctor', icon: 'mdi-doctor',         color: 'deep-orange' },
        5: { label: 'Rescheduled',         icon: 'mdi-calendar-sync',  color: 'purple' },
        6: { label: 'No Show',             icon: 'mdi-account-off',    color: 'grey darken-1' },
        7: { label: 'Expired',             icon: 'mdi-timer-off',      color: 'blue-grey' },
      };
      const isTerminal = [3, 4, 5, 6, 7].includes(s);
      const steps = [
        { label: 'Pending',   icon: 'mdi-clock-outline', color: 'warning', done: s > 0,               active: s === 0 },
        { label: 'Confirmed', icon: 'mdi-check',         color: 'primary', done: s === 2 || isTerminal, active: s === 1 },
      ];
      if (isTerminal) {
        const t = terminalMap[s];
        steps.push({ label: t.label, icon: t.icon, color: t.color, done: false, active: true });
      } else {
        steps.push({ label: 'Completed', icon: 'mdi-check-circle', color: 'success', done: s === 2, active: s === 2 });
      }
      return steps;
    },
  },
  mounted() {
    this.getUsers();
    this.getData();
  },
  methods: {
    toggleEditMode() {
      this.isEditMode = !this.isEditMode;
      // Show a message to the user about the mode change
      if (this.isEditMode) {
        this.$toast.success("Edit mode enabled. You can now modify the form.");
      } else {
        this.$toast.info("Read-only mode enabled. Form is now protected from changes.");
      }
    },

    async getUsers() {
      try {
        const response = await axios.get("appointments/users");
        const users = response.data.data;
        
        // Filter users by position - doctors and patients
        const doctorUsers = users.filter(user => 
          user.position && user.position.name && 
          user.position.name.toLowerCase().includes('doctor')
        );
        
        const patientUsers = users.filter(user => 
          user.position && user.position.name && 
          user.position.name.toLowerCase().includes('patient')
        );
        
        // Populate doctors dropdown with users having doctor positions
        this.doctors = doctorUsers.map(user => ({
          id: user.id,
          text: user.name,
          value: user.id,
        }));
        
        // Populate patients dropdown with users having patient positions
        this.patients = patientUsers.map(user => ({
          id: user.id,
          text: user.name,
          value: user.id,
        }));
        
        // If no users found with specific positions, show all users in both dropdowns as fallback
        if (this.doctors.length === 0 && this.patients.length === 0) {
          this.doctors = users.map(user => ({
            id: user.id,
            text: user.name,
            value: user.id,
          }));
          
          this.patients = users.map(user => ({
            id: user.id,
            text: user.name,
            value: user.id,
          }));
        }
      } catch (error) {
        console.log(error);
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
      }
    },
    async getData() {
      this.isLoading = true;
      let encryptedId = this.$route.params.appointmentId;
      try {
        const response = await axios.get(`/appointments/${encryptedId}`);
        this.form = response.data.data;
        
        // Parse appointment time for date/time pickers
        if (this.form.appointment_time) {
          const { date, time } = splitIstDateTime(this.form.appointment_time);
          this.appointmentDate = date;
          this.appointmentTime = time;
        }
        
      } catch (error) {
        console.error("Error fetching appointment data:", error);
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        } else {
          this.$router.push("/appointments");
        }
      }
      this.isLoading = false;
    },
    updateDateTime() {
      if (this.appointmentDate && this.appointmentTime) {
        this.form.appointment_time = combineIstDateTime(this.appointmentDate, this.appointmentTime);
        this.dateTimeMenu = false;
      }
    },
    clearAppointmentTime() {
      this.form.appointment_time = "";
      this.appointmentDate = "";
      this.appointmentTime = "";
    },
    async save() {
      try {
        this.isSaving = true;
        this.errors = {};
        
        await axios.put(`/appointments/${this.$route.params.appointmentId}`, this.form);
        this.isSaving = false;
        await this.saveUserTimestamp(this.$route.params.appointmentId, this.form);
        this.$router.push("/appointments");
      } catch (error) {
        this.isSaving = false;
        if (error.response && error.response.data.errors) {
          this.errors = error.response.data.errors;
        } else {
          console.error("Error updating appointment:", error);
        }
      }
    },
    confirmDelete() {
      this.deleteDialog = true;
    },
    async deleteAppointment() {
      try {
        await axios.post(`/appointments/delete/${this.$route.params.appointmentId}`);
        this.deleteDialog = false;
        this.$router.push("/appointments");
      } catch (error) {
        console.error("Error deleting appointment:", error);
        if (error.response && error.response.status === 401) {
          window.location.href = "/auth/login";
        }
      }
    },
  },
};
</script>

<style scoped>
/* Professional Page Header */
.page-header-card {
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-left: 4px solid var(--v-primary-base);
}

.page-title {
  font-size: 1.25rem;
  font-weight: 600;
  line-height: 1.1;
  margin-bottom: 0.15rem;
}

.page-subtitle {
  font-size: 0.75rem;
  line-height: 1.2;
}

.back-button {
  min-width: 100px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  font-size: 0.75rem;
}

/* Professional Form Card */
.form-card {
  background: #ffffff;
  border-radius: 8px;
  overflow: hidden;
}

.form-header {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-bottom: 1px solid #dee2e6;
}

.form-title {
  font-size: 1rem;
  font-weight: 600;
  color: var(--v-primary-base);
  margin: 0;
}

.form-content {
  background: #ffffff;
}

/* Form Actions */
.form-actions {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
  border-top: 1px solid #dee2e6;
}

.action-button {
  min-width: 90px;
  height: 32px;
  font-weight: 500;
  text-transform: none;
  border-radius: 4px;
  font-size: 0.75rem;
}
</style>